import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { PollResponse, ResponseValue, Voter } from '../../types';
import React, { useState } from 'react';
import { Timestamp, addDoc, collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Papa from 'papaparse';
import { db } from '../../firebase';
import { styled } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';

interface Responses {
  [question: string]: ResponseValue;
}

interface ImportError {
  type: string;
  source: string;
  message: string;
  voterName?: string;
  timestamp: Timestamp;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const SocorroCSVImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<string[][]>([]);

  const dashboardId = 'SURIGAO DEL NORTE_DISTRICT 1';

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      if (uploadedFile.type !== 'text/csv') {
        setError('Please upload a CSV file');
        return;
      }
      setFile(uploadedFile);
      setError(null);
      parseCSV(uploadedFile);
    }
  };

  const parseCSV = (csvFile: File) => {
    Papa.parse(csvFile, {
      delimiter: ',',
      quoteChar: '"',
      skipEmptyLines: true,
      complete: (results) => {
        // Limit preview to first 5 rows
        setPreviewData(results.data.slice(0, 5) as string[][]);
      },
      error: (error) => {
        setError(`CSV parsing error: ${error.message}`);
      }
    });
  };

  const logImportError = async (errorData: ImportError) => {
    try {
      const importErrorsRef = collection(db, 'importErrors');
      await addDoc(importErrorsRef, errorData);
    } catch (error) {
      console.error('Error logging import error: ', error);
    }
  };

  const submitPollResponse = async (responses: Responses) => {
    try {
      const responseId = uuidv4();
      const pollResponse: PollResponse = {
        id: responseId,
        pollId: "y1CTxIqawgTUoSGFJU8f",
        pollTitle: "Socorro (VIS)",
        responses,
        submittedAt: Timestamp.now(),
        submittedBy: "Socorro Import 2",
      };
      
      const responseRef = doc(collection(db, 'pollResponses'), responseId);
      await setDoc(responseRef, pollResponse);
    } catch (error) {
      console.error('Error submitting poll response: ', error);
      setError('Error submitting poll response: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const getVoter = async (voterName: string): Promise<Voter | null> => {
    try {
      const votersColRef = collection(db, 'voters');
      const q = query(votersColRef, where('name', '==', voterName));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data() as Voter;
      }
      
      return null;
    } catch (error) {
      console.error('Error checking if voter exists: ', error);
      setError('Error checking if voter exists: ' + (error instanceof Error ? error.message : 'Unknown error'));
      return null;
    }
  };

  const wasPolledBefore = async (voter: Voter): Promise<boolean> => {
    try {
      const latestPollResponsePath = 
        `tabulationsV2/${dashboardId}/regions/${voter.region}/`
        + `provinces/${voter.province}/districts/${voter.district}/`
        + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
        + `precincts/${voter.precinct}/voters/${voter.name}`;

      const latestPollResponseRef = doc(db, latestPollResponsePath);
      const latestPollResponseDoc = await getDoc(latestPollResponseRef);
      return latestPollResponseDoc.exists();
    } catch (error) {
      console.error('Error checking if voter was polled before: ', error);
      setError('Error checking if voter was polled before: ' + (error instanceof Error ? error.message : 'Unknown error'));
      return false;
    }
  };

  const handleImport = async () => {
    debugger;
    if (!file) return;

    setLoading(true);
    setError(null);

    const batchRunTimestamp = Timestamp.now();

    Papa.parse(file, {
      delimiter: ',',
      quoteChar: '"',
      skipEmptyLines: true,
      complete: (results) => {
        // Use a separate async function to handle the import logic
        const processImport = async () => {
          try {
            // Assuming first row is headers
            const dataRows = results.data.slice(1) as string[][];

            for (const row of dataRows) {
              const voterName: string = row[1];
              if (!voterName) {
                continue;
              }
              const voter = await getVoter(voterName);
              if (!voter) {
                // Log error for skipped voter
                await logImportError({
                  type: 'VOTER_NOT_FOUND',
                  source: 'Socorro Poll Import',
                  message: `Voter not found in database`,
                  voterName,
                  timestamp: batchRunTimestamp
                });
                console.error(`Voter not found in database: ${voterName}`);
                continue;
              }

              const color = row[3].toUpperCase(); // Updated index to match the sample data

              if (color.startsWith("BLUE")) {
                const abante: Responses = {
                  "DISTRICT 1 REPRESENTATIVE": "Lucille Sering",
                  "MAYOR": "Etoc Galanida",
                  "PARTY": "Abante Surigao",
                  "PROVINCIAL GOVERNOR": "Lyndon Barbers",
                  "PROVINCIAL VICE GOVERNOR": "Geed Gokiangkee",
                  "VOTER": voterName,
                };
                await submitPollResponse(abante);
                console.log(`Abante Surigao: ${voterName}`);
              } else if (color === "RED") {
                const padajon: Responses = {
                  "DISTRICT 1 REPRESENTATIVE": "Lalo Matugas",
                  "MAYOR": "Reizl Timcang",
                  "PARTY": "Padajon",
                  "PROVINCIAL GOVERNOR": "Bingo Matugas",
                  "PROVINCIAL VICE GOVERNOR": "Bully Navarro IV",
                  "VOTER": voterName,
                };
                await submitPollResponse(padajon);
                console.log(`Padajon: ${voterName}`);
              } else if (color === "YELLOW")  {
                console.log(`Undecided: ${voterName}`);
              } else if (color === "BLACK")  {
                console.log(`Deceased: ${voterName}`);
              } else if (color === "GREEN")  {
                console.log(`Out-of-Town: ${voterName}`);
              } else if (color === "PINK")  {
                console.log(`Transferred: ${voterName}`);
              } else {
                // Log error for unknown voter preference
                await logImportError({
                  type: 'UNKNOWN_VOTER_PREFERENCE',
                  source: 'Socorro Poll Import 2',
                  message: `Unknown voter color preference: ${color}`,
                  voterName,
                  timestamp: batchRunTimestamp
                });
                console.error(`Unknown voter color preference: ${color} - ${voterName}`);
              }
            }

            alert('Socorro Poll CSV Import Successful');
            setLoading(false);
          } catch (err) {
            setError('Failed to import CSV');
            console.error(err);
            setLoading(false);
          }
        };

        // Call the async function
        processImport();
      },
      error: (error) => {
        setError(`CSV parsing error: ${error.message}`);
        setLoading(false);
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Socorro Municipality CSV Import
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        component="label"
        variant="contained"
        startIcon={<CloudUploadIcon />}
        disabled={loading}
      >
        Upload CSV
        <VisuallyHiddenInput 
          type="file" 
          accept=".csv"
          onChange={handleFileUpload}
        />
      </Button>

      {file && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">
            Selected File: {file.name}
          </Typography>

          {previewData.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {previewData[0].map((header, index) => (
                      <TableCell key={index}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.slice(1).map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={handleImport}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Import CSV'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SocorroCSVImport;
